import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import Joyride, { STATUS, ACTIONS } from 'react-joyride';
import { Box, Typography, Button, IconButton } from '@mui/material';
import { Close, ArrowForward, ArrowBack } from '@mui/icons-material';
import { getTourSteps } from './tourSteps';

const TourContext = createContext(null);

export const useTour = () => useContext(TourContext);

/**
 * Map tour IDs to the sidebar section that must be active for the tour
 * targets to be rendered. Tours not listed here work on any section
 * (e.g. 'welcome' targets global elements like sidebar, header).
 */
const TOUR_SECTION_MAP = {
  cad: 'cad',
  requirements: 'requirements',
  documents: 'documents',
  orchestrator: 'orchestrator',
  compliance: 'compliance',
  validation: 'validation',
  analytics: 'projects',          // Analytics is a tab inside ProjectView
  'knowledge-base': 'projects',   // KB is a tab inside ProjectView
  // 'welcome' and 'chat' targets exist on all sections
};

/** Custom tooltip styled with MUI */
const TourTooltip = ({ continuous, index, step, backProps, closeProps, primaryProps, skipProps, tooltipProps, size }) => (
  <Box
    {...tooltipProps}
    sx={{
      bgcolor: 'background.paper',
      borderRadius: 2,
      boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
      maxWidth: 380,
      p: 0,
      border: '1px solid',
      borderColor: 'divider',
      overflow: 'hidden',
    }}
  >
    {/* Header */}
    <Box sx={{
      px: 2.5, py: 1.5,
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    }}>
      <Typography variant="subtitle2" sx={{ color: 'white', fontWeight: 600 }}>
        {step.title}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)' }}>
          {index + 1} / {size}
        </Typography>
        <IconButton size="small" {...closeProps} sx={{ color: 'white', p: 0.25 }}>
          <Close sx={{ fontSize: 16 }} />
        </IconButton>
      </Box>
    </Box>

    {/* Body */}
    <Box sx={{ px: 2.5, py: 2 }}>
      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>
        {step.content}
      </Typography>
    </Box>

    {/* Footer */}
    <Box sx={{
      px: 2.5, py: 1.5,
      borderTop: '1px solid', borderColor: 'divider',
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    }}>
      <Button size="small" {...skipProps} sx={{ textTransform: 'none', color: 'text.secondary' }}>
        Skip tour
      </Button>
      <Box sx={{ display: 'flex', gap: 1 }}>
        {index > 0 && (
          <Button size="small" variant="outlined" startIcon={<ArrowBack sx={{ fontSize: 14 }} />}
            {...backProps} sx={{ textTransform: 'none', fontSize: '0.8rem' }}>
            Back
          </Button>
        )}
        <Button size="small" variant="contained" endIcon={continuous ? <ArrowForward sx={{ fontSize: 14 }} /> : null}
          {...primaryProps}
          sx={{
            textTransform: 'none', fontSize: '0.8rem',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          }}>
          {continuous ? 'Next' : 'Done'}
        </Button>
      </Box>
    </Box>
  </Box>
);

export const TourProvider = ({ children }) => {
  const [tourSteps, setTourSteps] = useState([]);
  const [runTour, setRunTour] = useState(false);
  const [activeTourId, setActiveTourId] = useState(null);
  const [pendingTourId, setPendingTourId] = useState(null);
  const navigateRef = useRef(null);

  /**
   * Register a section navigation callback from AppContent.
   * Called once on mount so tours can navigate before starting.
   */
  const setNavigateToSection = useCallback((fn) => {
    navigateRef.current = fn;
  }, []);

  const startTour = useCallback((tourId) => {
    const steps = getTourSteps(tourId);
    if (steps.length === 0) return;

    // Navigate to the required section first (if needed)
    const requiredSection = TOUR_SECTION_MAP[tourId];
    if (requiredSection && navigateRef.current) {
      navigateRef.current(requiredSection);
    }

    setTourSteps(steps);
    setActiveTourId(tourId);
    setPendingTourId(tourId);
  }, []);

  // Wait for tour target elements to appear in the DOM before starting Joyride.
  // Lazy-loaded sections may not have rendered yet after navigation.
  useEffect(() => {
    if (!pendingTourId || tourSteps.length === 0) return;

    const firstTarget = tourSteps[0]?.target;
    if (!firstTarget) {
      setPendingTourId(null);
      setRunTour(true);
      return;
    }

    const poll = setInterval(() => {
      if (document.querySelector(firstTarget)) {
        clearInterval(poll);
        setPendingTourId(null);
        setRunTour(true);
      }
    }, 100);

    const timeout = setTimeout(() => {
      clearInterval(poll);
      setPendingTourId(null);
    }, 5000);

    return () => { clearInterval(poll); clearTimeout(timeout); };
  }, [pendingTourId, tourSteps]);

  const stopTour = useCallback(() => {
    setRunTour(false);
    setActiveTourId(null);
  }, []);

  const handleCallback = (data) => {
    const { status, action } = data;
    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status) || action === ACTIONS.CLOSE) {
      // Mark as completed in localStorage
      if (activeTourId) {
        const completed = JSON.parse(localStorage.getItem('completedTours') || '{}');
        completed[activeTourId] = true;
        localStorage.setItem('completedTours', JSON.stringify(completed));
      }
      stopTour();
    }
  };

  const isTourCompleted = (tourId) => {
    const completed = JSON.parse(localStorage.getItem('completedTours') || '{}');
    return !!completed[tourId];
  };

  return (
    <TourContext.Provider value={{ startTour, stopTour, activeTourId, isTourCompleted, setNavigateToSection }}>
      {children}
      <Joyride
        steps={tourSteps}
        run={runTour}
        continuous
        showSkipButton
        showProgress
        scrollToFirstStep
        disableOverlayClose
        tooltipComponent={TourTooltip}
        callback={handleCallback}
        styles={{
          options: {
            zIndex: 10000,
            arrowColor: '#fff',
            overlayColor: 'rgba(0, 0, 0, 0.5)',
          },
        }}
        floaterProps={{
          disableAnimation: true,
        }}
      />
    </TourContext.Provider>
  );
};

export default TourProvider;
